/**
 * HEIC/HEIF Conversion Service
 *
 * Converts HEIC/HEIF images to JPEG for browser compatibility.
 *
 * Uses libheif-js/wasm-bundle: a modern (v1.19+) Emscripten WASM build of
 * libheif that supports HEVC-encoded HEIC files from modern iPhones.
 * heic2any (v0.0.4, 2019) bundled an old libheif that fails on current iPhone
 * HEIC files with "ERR_LIBHEIF format not supported".
 *
 * wasm-bundle embeds the WASM binary inside the JS, so Vite/esbuild never
 * need to handle a separate .wasm asset.
 */

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — no type definitions for libheif-js
import * as libheifModule from 'libheif-js/wasm-bundle';

// wasm-bundle is already-initialised CJS: module.exports = factory().
// After Vite's CJS→ESM interop the object lands on .default.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getLibheif = (): any => (libheifModule as any).default ?? libheifModule;

/**
 * Check if a URL points to a HEIC/HEIF file based on the path extension.
 */
export const isHeicUrl = (url: string): boolean => {
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase();
  return ext === 'heic' || ext === 'heif';
};

/**
 * Check if a file is a HEIC/HEIF image based on extension and MIME type.
 */
export const isHeicFile = (file: File): boolean => {
  const ext = file.name.toLowerCase().split('.').pop();
  return (
    ext === 'heic' ||
    ext === 'heif' ||
    file.type === 'image/heic' ||
    file.type === 'image/heif'
  );
};

/**
 * Check if a Blob's MIME type indicates HEIC/HEIF or is ambiguous enough to try.
 */
export const isHeicBlob = (blob: Blob): boolean => {
  return (
    blob.type === 'image/heic' ||
    blob.type === 'image/heif' ||
    blob.type === '' ||
    blob.type === 'application/octet-stream'
  );
};

/**
 * Convert a Blob known to be HEIC/HEIF to a JPEG Blob.
 * Shared by convertHeicToJpeg and HeicSafeImage's error fallback.
 *
 * Uses libheif-js (libheif 1.19+) which supports HEVC-encoded HEIC from
 * modern iPhones. Decodes to raw RGBA via the libheif decoder, then paints
 * onto a Canvas and exports as JPEG.
 */
export const convertHeicBlobToJpeg = async (blob: Blob, quality = 0.85): Promise<Blob> => {
  const lh = getLibheif();
  const buffer = await blob.arrayBuffer();
  const decoder = new lh.HeifDecoder();
  const data: unknown[] = decoder.decode(new Uint8Array(buffer));

  if (!data || data.length === 0) {
    throw new Error('No images found in HEIC file');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const image = data[0] as any;
  const width: number = image.get_width();
  const height: number = image.get_height();

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  const imageData = ctx.createImageData(width, height);

  await new Promise<void>((resolve, reject) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    image.display(imageData, (displayData: any) => {
      if (!displayData) {
        reject(new Error('HEIF processing error'));
        return;
      }
      ctx.putImageData(displayData, 0, 0);
      resolve();
    });
  });

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob returned null'))),
      'image/jpeg',
      quality
    );
  });
};

/**
 * Fetch a HEIC/HEIF URL, convert to JPEG, and return an object URL.
 * Returns the original URL unchanged if it is not HEIC/HEIF.
 * The caller is responsible for revoking the returned blob URL when done.
 */
export const convertHeicUrlToObjectUrl = async (url: string): Promise<string> => {
  if (!isHeicUrl(url)) return url;

  const response = await fetch(url);
  const blob = await response.blob();
  const jpegBlob = await convertHeicBlobToJpeg(blob);
  return URL.createObjectURL(jpegBlob);
};

/**
 * Convert a HEIC/HEIF File to a JPEG File.
 * Returns the original file unchanged if it is not HEIC/HEIF.
 */
export const convertHeicToJpeg = async (
  file: File,
  quality: number = 0.85
): Promise<File> => {
  if (!isHeicFile(file)) {
    return file;
  }

  console.log(`[HEIC] Converting ${file.name} (${file.size} bytes) to JPEG...`);

  try {
    const resultBlob = await convertHeicBlobToJpeg(file, quality);
    const jpegName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    const jpegFile = new File([resultBlob], jpegName, { type: 'image/jpeg' });
    console.log(`[HEIC] Converted: ${file.size} -> ${jpegFile.size} bytes`);
    return jpegFile;
  } catch (error) {
    console.error('[HEIC] Conversion failed:', error);
    throw new Error(
      'HEIC-Konvertierung fehlgeschlagen. Bitte konvertiere das Bild manuell zu JPEG.'
    );
  }
};
